// Theme preference lives only in this device's localStorage — there is no server
// round-trip, which is what keeps the first paint free of a flash.
//
// The same key and the same resolution rule are duplicated in the bootstrap script in
// _document.tsx. That script has to be inline and synchronous (it runs before any
// stylesheet), so it cannot import this module. Keep the two in sync.

export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'theme'
export const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

export const THEME_PREFERENCES: ThemePreference[] = ['system', 'light', 'dark']

// Dark mode is in Beta, so nobody lands in it without opting in — not even with an OS
// set to dark. Flip to 'system' once it leaves Beta; the bootstrap in _document.tsx
// hard-codes the same fallback.
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light'

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean
): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light'
  return preference
}

export function readPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    return THEME_PREFERENCES.includes(raw as ThemePreference)
      ? (raw as ThemePreference)
      : DEFAULT_THEME_PREFERENCE
  } catch {
    // Safari private mode throws on access.
    return DEFAULT_THEME_PREFERENCE
  }
}

export function writePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Losing the preference is better than breaking the page.
  }
}

export function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(SYSTEM_DARK_QUERY).matches
}

export function applyTheme(resolved: ResolvedTheme): void {
  const el = document.documentElement
  el.classList.toggle('dark', resolved === 'dark')
  // Native scrollbars, <select> and other form controls follow this, not the class.
  el.style.colorScheme = resolved
}
